import * as React from 'react';
import { useTranslation } from 'react-i18next';
import DocumentTitle from 'react-document-title';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { Empty, Spin } from 'src/design-system/components/atoms';
import { TURNSTILE_SITE_KEY } from 'src/globals';
import {
  extractTrackingConsignments,
  fetchOrgTrackDetails,
  fetchPublicTracking,
  mapOrgTrackConfig,
} from './GlobalTrack.api';
import {
  CAPTCHA_ACTION,
  CAPTCHA_READY_TIMEOUT_MS,
  DEFAULT_ORG_TRACK_CONFIG,
  GLOBAL_TRACK_PATH,
  SearchByOption,
} from './GlobalTrack.constants';
import { GlobalTrackProps, OrgTrackConfig, TrackingConsignment, TrackSearchPayload } from './GlobalTrack.types';
import { generateTrackQuery, parseTrackQuery } from './GlobalTrack.utils';
import { useGlobalTrackStyles } from './GlobalTrack.styles';
import TrackHeader from './TrackHeader/TrackHeader';
import SearchBox from './SearchBox/SearchBox';
import TrackResults from './TrackResults/TrackResults';

const GlobalTrack = (props: GlobalTrackProps) => {
  const { location, history } = props;
  const { t } = useTranslation();
  const classes = useGlobalTrackStyles();

  const turnstileRef = React.useRef<TurnstileInstance>();

  const [orgConfig, setOrgConfig] = React.useState<OrgTrackConfig>(DEFAULT_ORG_TRACK_CONFIG);
  const [isOrgConfigLoading, setIsOrgConfigLoading] = React.useState<boolean>(false);
  const [results, setResults] = React.useState<TrackingConsignment[]>([]);
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const [expandedRef, setExpandedRef] = React.useState<string | null>(null);

  const requestToken = React.useCallback(async (): Promise<string | undefined> => {
    const widget = turnstileRef.current;
    if (!widget) {
      return undefined;
    }
    try {
      widget.reset();
      const token = await widget.getResponsePromise(CAPTCHA_READY_TIMEOUT_MS);
      return token || undefined;
    } catch {
      return undefined;
    }
  }, []);

  // The URL query is the source of truth: submitting pushes a new query, and browser back/forward
  // re-parses and re-fetches — matching the source page's componentDidMount + componentWillReceiveProps.
  const activeSearch = React.useMemo(() => parseTrackQuery(location.search), [location.search]);

  React.useEffect(() => {
    const loadOrgConfig = async () => {
      setIsOrgConfigLoading(true)
      const response = await fetchOrgTrackDetails();
      if (response.isSuccess) {
        setOrgConfig(mapOrgTrackConfig(response.data));
      }
      setIsOrgConfigLoading(false)
    };
    loadOrgConfig();
  }, []);

  React.useEffect(() => {
    if (!activeSearch) {
      setResults([]);
      setIsFetching(false);
      return undefined;
    }

    let cancelled = false;
    setIsFetching(true);

    (async () => {
      let token: string | undefined;
      if (TURNSTILE_SITE_KEY) {
        token = await requestToken();
      }

      if (cancelled) {
        return;
      }

      const response = await fetchPublicTracking(activeSearch, token || undefined);
      if (cancelled) {
        return;
      }

      if (response.isSuccess) {
        setResults(extractTrackingConsignments(response.data));
      } else {
        setResults([]);
      }
      setIsFetching(false);
    })();

    return () => {
      cancelled = true;
    };
    // Keyed on location.key (a fresh id per history entry) so re-submitting the same numbers
    // re-fetches — the hooks equivalent of the source's location-identity trigger.
  }, [location.key, activeSearch, requestToken]);

  const handleSearch = (payload: TrackSearchPayload) => {
    history.push(`${GLOBAL_TRACK_PATH}?${generateTrackQuery(payload)}`);
  };

  const handleToggle = (referenceNumber: string) => {
    setExpandedRef((prev) => (prev === referenceNumber ? null : referenceNumber));
  };

  const hideCourierPartner = activeSearch?.searchBy === SearchByOption.REFERENCE_NUMBER;

  const renderResults = () => {
    if (!activeSearch) {
      return null;
    }

    if (isFetching) {
      return (
        <div className={classes.loaderWrapper}>
          <Spin size="large" />
        </div>
      );
    }

    if (!results.length) {
      return (
        <div className={classes.emptyWrapper}>
          <Empty description={t('No Result Found')} />
        </div>
      );
    }

    return (
      <TrackResults
        items={results}
        expandedRef={expandedRef}
        onToggle={handleToggle}
        hideCourierPartner={hideCourierPartner}
      />
    );
  };

  const backgroundStyle = orgConfig.trackingPageBackgroundUrl
    ? { backgroundImage: `url(${orgConfig.trackingPageBackgroundUrl})` }
    : undefined;

  const captchaWidget = TURNSTILE_SITE_KEY ? (
    <Turnstile
      ref={turnstileRef}
      siteKey={TURNSTILE_SITE_KEY}
      options={{ action: CAPTCHA_ACTION, appearance: 'interaction-only', retry: 'never', refreshExpired: 'never', theme: 'light' }}
      className={classes.captchaWidget}
      onError={() => undefined}
    />
  ) : null;

  if (isOrgConfigLoading) {
    return (
      <div className={classes.page}>
        <DocumentTitle title={t('Track Shipment')} />
        {captchaWidget}
        <div className={classes.pageLoader}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.page} style={backgroundStyle}>
      <DocumentTitle title={t('Track Shipment')} />
      {captchaWidget}
      <TrackHeader orgConfig={orgConfig} />

      <div className={classes.content}>
        <SearchBox
          initData={activeSearch}
          onSubmit={handleSearch}
          disableSubmit={isFetching}
          orgConfig={orgConfig}
        />

        {renderResults()}
      </div>
    </div>
  );
};

export default GlobalTrack;
